import React, { useMemo } from "react";
import { Calendar, CheckCircle2, ChevronRight, Clock, MapPin } from "lucide-react";

export default function ScheduleMobileScheduleList({
  filteredSchedulesBase,
  formatDateLocal,
  getTypeColor,
  getTypeIcon,
  onOpenDetail,
  selectedDate,
  selectedType,
}) {
  const groupedSchedules = useMemo(() => {
    const displaySchedules = filteredSchedulesBase
      .filter((schedule) => selectedType === "all" || schedule.type === selectedType)
      .filter((schedule) => !selectedDate || schedule.date === (typeof selectedDate === "string" ? selectedDate : formatDateLocal(selectedDate)));

    const grouped = displaySchedules.reduce((acc, schedule) => {
      if (!acc[schedule.date]) acc[schedule.date] = [];
      acc[schedule.date].push(schedule);
      return acc;
    }, {});

    return {
      grouped,
      sortedDates: Object.keys(grouped).sort(),
    };
  }, [filteredSchedulesBase, formatDateLocal, selectedDate, selectedType]);

  if (groupedSchedules.sortedDates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-300">
        <Calendar size={48} className="mb-3 opacity-20" />
        <p className="text-sm font-medium">일정이 없습니다</p>
      </div>
    );
  }

  return groupedSchedules.sortedDates.map((date) => (
    <div key={date}>
      <div className="sticky top-0 bg-[#F0F0EB]/95 backdrop-blur-md py-2 text-sm font-bold text-[#354F38] border-b border-white/10 z-10">
        {(() => {
          const d = new Date(`${date}T00:00:00`);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          if (d.getTime() === today.getTime()) return "오늘";
          if (d.getTime() === tomorrow.getTime()) return "내일";
          return `${d.getMonth() + 1}월 ${d.getDate()}일`;
        })()}
      </div>
      <div className="space-y-2 pb-4 pt-2">
        {groupedSchedules.grouped[date].map((schedule) => {
          const colors = getTypeColor(schedule.type);
          return (
            <div
              key={schedule.id}
              onClick={() => onOpenDetail(schedule)}
              className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm active:scale-[0.98] transition-all cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0`}>
                  <img src={getTypeIcon(schedule.type)} alt="" className="w-7 h-7 object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-800 truncate">{schedule.title}</h3>
                    {schedule.isCompleted && (
                      <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {schedule.startTime && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock size={12} />
                        <span>{schedule.startTime} {schedule.endTime && `~${schedule.endTime}`}</span>
                      </div>
                    )}
                    {schedule.location && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <MapPin size={12} />
                        <span className="truncate">{schedule.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRight size={18} className="text-gray-300 flex-shrink-0" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ));
}
